import { type CSSProperties, type DragEvent, type MouseEvent as ReactMouseEvent, type RefObject } from "react"
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronLeft, Copy, Move, Plus, Trash2 } from "lucide-react"

import type {
  ReportTemplateFlowSectionDto,
  ReportTemplateFrameDto,
  ReportLayoutWidgetDto,
  ReportLayoutWidgetType,
  ReportTemplateLayoutDto,
} from "../../api/reportTemplateService"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Checkbox } from "shared/ui/checkbox"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import {
  ACTIVE_WIDGET_Z_INDEX,
  DEFAULT_REPORT_FRAME,
  estimateFieldRenderedHeight,
  MIN_FLOW_SECTION_HEIGHT,
  WIDGET_TEMPLATES,
  buildWidgetBorderStyle,
  estimateTableRenderedHeight,
  getWidgetFlowSectionId,
  getWidgetLocked,
  toFiniteNumber,
  getWidgetCornerRadius,
  getWidgetImageFit,
  getWidgetLineConfig,
  getWidgetPositionMode,
  getWidgetTextAlign,
  getWidgetTypographyConfig,
  getWidgetVerticalAlign,
  isLikelyImageUrl,
  resolveWidgetLabel,
  toWidgetTitle,
  type TableColumnWidthMap,
  type ResizeMode,
} from "./editorUtils"

export interface LayoutCanvasWorkspaceProps {
  layout: ReportTemplateLayoutDto
  layoutViewMode: "design" | "preview"
  designSection: "body" | "header" | "footer"
  activeBodyFlowSectionId: string
  designBodyPage: number
  manualPageCount: number
  flowSections: ReportTemplateFlowSectionDto[]
  designWidgetRenderYMap: Record<string, number>
  designFlowSectionBoxes: Array<{
    sectionId: string
    name: string
    breakMode: "flow" | "newPage"
    isCollapsed: boolean
    minHeight: number
    x: number
    y: number
    width: number
    height: number
    widgetCount: number
  }>
  bodyWidgetsForDesignPage: ReportLayoutWidgetDto[]
  designSectionOffsetY: number
  fullCanvasMetrics: { width: number; height: number }
  editCanvasMetrics: { width: number; height: number }
  headerEnabled: boolean
  footerEnabled: boolean
  headerHeight: number
  footerHeight: number
  previewPageIndex: number
  previewTotalPages: number
  canvasRef: RefObject<HTMLDivElement | null>
  sortedWidgets: ReportLayoutWidgetDto[]
  previewBodyRenderEntries: Array<{
    widget: ReportLayoutWidgetDto
    renderY: number
    renderHeight?: number
    tableRows?: string[][]
  }>
  previewHeaderWidgets: ReportLayoutWidgetDto[]
  previewFooterWidgets: ReportLayoutWidgetDto[]
  selectedWidgetId: string | null
  movingWidgetId: string | null
  movingSectionId: string | null
  resizingSectionId: string | null
  resizingWidgetId: string | null
  fieldLabelMap: Map<string, string>
  getFieldDisplay: (fieldKey: string, datasourceId?: string) => string
  getTableColumnLabel: (widget: ReportLayoutWidgetDto, columnKey: string) => string
  getTableColumnWidths: (widget: ReportLayoutWidgetDto, columns: string[]) => TableColumnWidthMap
  onTemplateDragStart: (widgetType: ReportLayoutWidgetType, event: DragEvent<HTMLButtonElement>) => void
  onCreateWidgetAt: (
    widgetType: ReportLayoutWidgetType,
    x: number,
    y: number,
    options?: { flowSectionId?: string; manualPage?: number },
  ) => void
  onDesignSectionChange: (value: "body" | "header" | "footer") => void
  onDesignBodyPageChange: (page: number) => void
  onManualPageCountChange: (count: number) => void
  onAddFlowSection: () => void
  onUpdateFlowSection: (sectionId: string, patch: Partial<ReportTemplateFlowSectionDto>) => void
  onMoveFlowSection: (sectionId: string, direction: "up" | "down") => void
  onRemoveFlowSection: (sectionId: string) => void
  onPageSizeChange: (value: ReportTemplateLayoutDto["pageSize"]) => void
  onOrientationChange: (value: ReportTemplateLayoutDto["orientation"]) => void
  onHeaderEnabledChange: (enabled: boolean) => void
  onFooterEnabledChange: (enabled: boolean) => void
  onHeaderHeightChange: (height: number) => void
  onFooterHeightChange: (height: number) => void
  onFrameChange: (patch: Partial<ReportTemplateFrameDto>) => void
  snapToGrid: boolean
  gridSize: number
  onSnapToGridChange: (enabled: boolean) => void
  onGridSizeChange: (size: number) => void
  onPreviewPageChange: (pageIndex: number) => void
  onCanvasDrop: (
    event: DragEvent<HTMLDivElement>,
    targetSection?: "body" | "header" | "footer",
    targetCanvasMetrics?: { width: number; height: number },
    options?: { flowSectionId?: string; manualPage?: number },
  ) => void
  onCanvasDragOver: (event: DragEvent<HTMLDivElement>) => void
  onRemoveWidget: (widgetId: string) => void
  onDuplicateWidget: (widgetId: string) => void
  onWidgetTransferDragStart: (
    sourceSection: "body" | "header" | "footer",
    widgetId: string,
    widgetType: ReportLayoutWidgetType,
    widgetWidth: number,
    widgetHeight: number,
    event: DragEvent<HTMLElement>,
  ) => void
  onActiveBodyFlowSectionChange: (sectionId: string) => void
  onCanvasClick: () => void
  onWidgetMouseDown: (widget: ReportLayoutWidgetDto, event: ReactMouseEvent<HTMLDivElement>) => void
  onFlowSectionMouseDown: (sectionId: string, event: ReactMouseEvent<HTMLDivElement>) => void
  onFlowSectionResizeMouseDown: (sectionId: string, event: ReactMouseEvent<HTMLDivElement>) => void
  onToggleFlowSectionCollapsed: (sectionId: string) => void
  onWidgetClick: (widgetId: string) => void
  onResizeMouseDown: (
    widget: ReportLayoutWidgetDto,
    event: ReactMouseEvent<HTMLDivElement>,
    mode: ResizeMode,
  ) => void
}

export function LayoutCanvasWorkspace({
  layout,
  layoutViewMode,
  designSection,
  activeBodyFlowSectionId,
  designBodyPage,
  manualPageCount,
  flowSections,
  designWidgetRenderYMap,
  designFlowSectionBoxes,
  bodyWidgetsForDesignPage,
  designSectionOffsetY,
  fullCanvasMetrics,
  editCanvasMetrics,
  headerEnabled,
  footerEnabled,
  headerHeight,
  footerHeight,
  previewPageIndex,
  previewTotalPages,
  canvasRef,
  sortedWidgets,
  previewBodyRenderEntries,
  previewHeaderWidgets,
  previewFooterWidgets,
  selectedWidgetId,
  movingWidgetId,
  movingSectionId,
  resizingSectionId,
  resizingWidgetId,
  fieldLabelMap,
  getFieldDisplay,
  getTableColumnLabel,
  getTableColumnWidths,
  onTemplateDragStart,
  onCreateWidgetAt,
  onDesignSectionChange,
  onDesignBodyPageChange,
  onManualPageCountChange,
  onAddFlowSection,
  onUpdateFlowSection,
  onMoveFlowSection,
  onRemoveFlowSection,
  onPageSizeChange,
  onOrientationChange,
  onHeaderEnabledChange,
  onFooterEnabledChange,
  onHeaderHeightChange,
  onFooterHeightChange,
  onFrameChange,
  snapToGrid,
  gridSize,
  onSnapToGridChange,
  onGridSizeChange,
  onPreviewPageChange,
  onCanvasDrop,
  onCanvasDragOver,
  onRemoveWidget,
  onDuplicateWidget,
  onWidgetTransferDragStart,
  onActiveBodyFlowSectionChange,
  onCanvasClick,
  onWidgetMouseDown,
  onFlowSectionMouseDown,
  onFlowSectionResizeMouseDown,
  onToggleFlowSectionCollapsed,
  onWidgetClick,
  onResizeMouseDown,
}: LayoutCanvasWorkspaceProps) {
  const designBodyMode = layoutViewMode === "design" && designSection === "body"
  const bodyOffsetY = headerEnabled ? headerHeight : 0
  const footerOffsetY = fullCanvasMetrics.height - (footerEnabled ? footerHeight : 0)
  const designFooterOffsetY = designBodyMode ? bodyOffsetY + editCanvasMetrics.height : footerOffsetY
  const activeCanvasMetrics = {
    width: fullCanvasMetrics.width,
    height:
      layoutViewMode === "design"
        ? (
          designBodyMode
            ? Math.max(
              fullCanvasMetrics.height,
              designFooterOffsetY + (footerEnabled ? footerHeight : 0),
            )
            : fullCanvasMetrics.height
        )
        : fullCanvasMetrics.height,
  }
  const sectionLabel =
    designSection === "header" ? "Page Header" : designSection === "footer" ? "Page Footer" : "Page Body"
  const showDesignGrid = layoutViewMode === "design" && snapToGrid
  const designGridStyle: CSSProperties = {
    backgroundImage: `
      linear-gradient(to right, rgba(59, 130, 246, 0.16) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(59, 130, 246, 0.16) 1px, transparent 1px)
    `,
    backgroundSize: `${gridSize}px ${gridSize}px`,
    backgroundPosition: "0 0",
  }
  const frame = {
    ...DEFAULT_REPORT_FRAME,
    ...(layout.frame ?? {}),
  }
  const framePageCount =
    layoutViewMode === "design" && designBodyMode
      ? Math.max(1, Math.ceil(activeCanvasMetrics.height / fullCanvasMetrics.height))
      : 1
  const frameAreaTopOffsetPerPage = frame.scope === "body" ? bodyOffsetY : 0
  const frameAreaHeightPerPage =
    frame.scope === "body"
      ? Math.max(40, fullCanvasMetrics.height - bodyOffsetY - (footerEnabled ? footerHeight : 0))
      : fullCanvasMetrics.height
  const primaryFlowSectionId = flowSections[0]?.sectionId || "section-default"
  const renderEntries: Array<{
    widget: ReportLayoutWidgetDto
    section: "body" | "header" | "footer"
    yOffset: number
    renderYOverride?: number
    renderHeightOverride?: number
    editable: boolean
    ghost: boolean
    tableRows?: string[][]
  }> =
    layoutViewMode === "design"
      ? [
          ...(headerEnabled
            ? (designSection === "header" ? sortedWidgets : previewHeaderWidgets).map((widget) => ({
                widget,
                section: "header" as const,
                yOffset: 0,
                editable: designSection === "header",
                ghost: designSection !== "header",
              }))
            : []),
          ...(designSection === "body" ? sortedWidgets : bodyWidgetsForDesignPage).map((widget) => ({
            widget,
            section: "body" as const,
            yOffset: bodyOffsetY,
            renderYOverride:
              designWidgetRenderYMap[widget.id] != null
                ? designWidgetRenderYMap[widget.id]
                : undefined,
            editable: designSection === "body",
            ghost: designSection !== "body",
          })),
          ...(footerEnabled
            ? (designSection === "footer" ? sortedWidgets : previewFooterWidgets).map((widget) => ({
                widget,
                section: "footer" as const,
                yOffset: designSection === "body" ? designFooterOffsetY : footerOffsetY,
                editable: designSection === "footer",
                ghost: designSection !== "footer",
              }))
            : []),
        ]
      : [
          ...(headerEnabled
            ? previewHeaderWidgets.map((widget) => ({
                widget,
                section: "header" as const,
                yOffset: 0,
                editable: false,
                ghost: false,
              }))
            : []),
          ...previewBodyRenderEntries.map((entry) => ({
            widget: entry.widget,
            section: "body" as const,
            yOffset: bodyOffsetY,
            renderYOverride: entry.renderY,
            renderHeightOverride: entry.renderHeight,
            editable: false,
            ghost: false,
            tableRows: entry.tableRows,
          })),
          ...(footerEnabled
            ? previewFooterWidgets.map((widget) => ({
                widget,
                section: "footer" as const,
                yOffset: footerOffsetY,
                editable: false,
                ghost: false,
              }))
            : []),
        ]
  const hasAnyWidgets =
    renderEntries.length > 0
  const handleSectionShortcut = (section: "body" | "header" | "footer") => {
    if (layoutViewMode !== "design") return
    if (section === "header" && !headerEnabled) return
    if (section === "footer" && !footerEnabled) return
    onDesignSectionChange(section)
  }
  const handleCanvasSurfaceClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (layoutViewMode === "design" && designSection === "body") {
      const rect = event.currentTarget.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const clickY = event.clientY - rect.top
      const hitSection = designFlowSectionBoxes.find((box) =>
        clickX >= box.x &&
        clickX <= box.x + box.width &&
        clickY >= box.y + designSectionOffsetY &&
        clickY <= box.y + designSectionOffsetY + box.height,
      )
      if (hitSection) {
        onActiveBodyFlowSectionChange(hitSection.sectionId)
      }
    }
    onCanvasClick()
  }
  const sectionSurfaceItems = [
    {
      key: "header" as const,
      label: "Header",
      enabled: headerEnabled,
      widgetCount: previewHeaderWidgets.length,
      top: 0,
      height: headerHeight,
      canvasMetrics: { width: fullCanvasMetrics.width, height: Math.max(60, headerHeight) },
      dropOptions: undefined,
      borderClass: "border-blue-200 bg-blue-50/60",
      ringClass: "ring-blue-300",
    },
    {
      key: "body" as const,
      label: `Body P${designBodyPage}`,
      enabled: true,
      widgetCount: bodyWidgetsForDesignPage.length,
      top: bodyOffsetY,
      height: designBodyMode ? editCanvasMetrics.height : Math.max(240, fullCanvasMetrics.height - bodyOffsetY - (footerEnabled ? footerHeight : 0)),
      canvasMetrics: { width: fullCanvasMetrics.width, height: editCanvasMetrics.height },
      dropOptions: {
        flowSectionId: activeBodyFlowSectionId,
        manualPage: designBodyPage,
      },
      borderClass: "border-emerald-200 bg-emerald-50/50",
      ringClass: "ring-emerald-300",
    },
    {
      key: "footer" as const,
      label: "Footer",
      enabled: footerEnabled,
      widgetCount: previewFooterWidgets.length,
      top: designFooterOffsetY,
      height: footerHeight,
      canvasMetrics: { width: fullCanvasMetrics.width, height: Math.max(60, footerHeight) },
      dropOptions: undefined,
      borderClass: "border-amber-200 bg-amber-50/60",
      ringClass: "ring-amber-300",
    },
  ]
  return (
    <>
      <Card className="self-start xl:sticky xl:top-4 xl:w-[300px] xl:min-w-[300px]">
        <CardHeader className="space-y-1 pb-3">
          <CardTitle className="text-base">Toolbox</CardTitle>
          <p className="text-xs text-gray-500">Drag a widget to canvas, or click to add.</p>
        </CardHeader>
        <CardContent className="max-h-[calc(100vh-8rem)] space-y-3 overflow-y-auto overflow-x-hidden pr-1">
          <details open className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Widgets</summary>
            <div className="mt-2 space-y-2">
              {WIDGET_TEMPLATES.map((item) => (
                <Button
                  key={item.type}
                  type="button"
                  variant="outline"
                  className="w-full justify-between"
                  draggable
                  onDragStart={(event) => onTemplateDragStart(item.type, event)}
                  onClick={() =>
                    onCreateWidgetAt(
                      item.type,
                      40,
                      40,
                      designSection === "body"
                        ? { flowSectionId: activeBodyFlowSectionId, manualPage: designBodyPage }
                        : undefined,
                    )
                  }
                >
                  <span>{item.label}</span>
                  <Move className="h-4 w-4 text-gray-400" />
                </Button>
              ))}
            </div>
          </details>

          <details open className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Section & Flow</summary>
            <div className="mt-2 space-y-2">
            <Label className="text-xs text-gray-500">Section</Label>
            <Select
              value={designSection}
              onValueChange={(value: "body" | "header" | "footer") => {
                if (value === "header" && !headerEnabled) return
                if (value === "footer" && !footerEnabled) return
                onDesignSectionChange(value)
              }}
              disabled={layoutViewMode !== "design"}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="body">Body</SelectItem>
                {headerEnabled ? <SelectItem value="header">Page Header</SelectItem> : null}
                {footerEnabled ? <SelectItem value="footer">Page Footer</SelectItem> : null}
              </SelectContent>
            </Select>
            {layoutViewMode === "design" && designSection === "body" ? (
              <div className="space-y-2 pt-1">
                <Label className="text-xs text-gray-500">Body Page</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onDesignBodyPageChange(Math.max(1, designBodyPage - 1))}
                    disabled={designBodyPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-gray-700">
                    {designBodyPage} / {manualPageCount}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onDesignBodyPageChange(Math.min(manualPageCount, designBodyPage + 1))}
                    disabled={designBodyPage >= manualPageCount}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Manual Pages</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={manualPageCount}
                    onChange={(event) => {
                      const parsed = Number.parseInt(event.target.value || "1", 10) || 1
                      onManualPageCountChange(Math.min(50, Math.max(1, parsed)))
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500">Flow Sections</Label>
                    <Button type="button" size="sm" variant="outline" onClick={onAddFlowSection} className="h-7 px-2">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="max-h-52 space-y-2 overflow-auto rounded-md border p-2">
                    {flowSections.map((section, index) => (
                      <div key={section.sectionId} className="space-y-2 rounded border p-2">
                        <div className="space-y-2">
                          <Input
                            value={section.name}
                            onChange={(event) => onUpdateFlowSection(section.sectionId, { name: event.target.value })}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Select
                              value={section.breakMode}
                              onValueChange={(value: "flow" | "newPage") =>
                                onUpdateFlowSection(section.sectionId, { breakMode: value })
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="flow">Flow</SelectItem>
                                <SelectItem value="newPage">New page</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              min={MIN_FLOW_SECTION_HEIGHT}
                              max={2400}
                              value={Math.max(MIN_FLOW_SECTION_HEIGHT, Math.floor(Number(section.minHeight) || 140))}
                              onChange={(event) => {
                                const parsed = Number.parseInt(event.target.value || "140", 10)
                                onUpdateFlowSection(section.sectionId, {
                                  minHeight: Number.isFinite(parsed) ? parsed : 140,
                                })
                              }}
                            />
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8"
                                onClick={() => onMoveFlowSection(section.sectionId, "up")}
                                disabled={index === 0}
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8"
                                onClick={() => onMoveFlowSection(section.sectionId, "down")}
                                disabled={index === flowSections.length - 1}
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8"
                                onClick={() => onRemoveFlowSection(section.sectionId)}
                                disabled={flowSections.length <= 1}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            </div>
          </details>

          <details open className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Header & Footer</summary>
            <div className="mt-2 space-y-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-gray-500">Header</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="layout-header-enabled"
                      checked={headerEnabled}
                      onCheckedChange={(checked) => onHeaderEnabledChange(Boolean(checked))}
                    />
                    <Label htmlFor="layout-header-enabled" className="cursor-pointer text-xs text-gray-600">
                      Enable
                    </Label>
                  </div>
                </div>
                {headerEnabled ? (
                  <div className="space-y-2 rounded-md border p-2">
                    <Input
                      type="number"
                      min={60}
                      max={360}
                      value={headerHeight}
                      onChange={(event) => onHeaderHeightChange(Number.parseInt(event.target.value || "120", 10) || 120)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant={designSection === "header" ? "default" : "outline"}
                      disabled={layoutViewMode !== "design"}
                      onClick={() => onDesignSectionChange("header")}
                      className="w-full"
                    >
                      Edit Header
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Enable header to place widgets in the top section.</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-gray-500">Footer</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="layout-footer-enabled"
                      checked={footerEnabled}
                      onCheckedChange={(checked) => onFooterEnabledChange(Boolean(checked))}
                    />
                    <Label htmlFor="layout-footer-enabled" className="cursor-pointer text-xs text-gray-600">
                      Enable
                    </Label>
                  </div>
                </div>
                {footerEnabled ? (
                  <div className="space-y-2 rounded-md border p-2">
                    <Input
                      type="number"
                      min={60}
                      max={360}
                      value={footerHeight}
                      onChange={(event) => onFooterHeightChange(Number.parseInt(event.target.value || "80", 10) || 80)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant={designSection === "footer" ? "default" : "outline"}
                      disabled={layoutViewMode !== "design"}
                      onClick={() => onDesignSectionChange("footer")}
                      className="w-full"
                    >
                      Edit Footer
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Enable footer to place widgets in the bottom section.</p>
                )}
              </div>
            </div>
          </details>

          <details className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Grid & Snap</summary>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2 rounded-md border px-2 py-2">
                <Checkbox
                  id="layout-snap-to-grid"
                  checked={snapToGrid}
                  onCheckedChange={(checked) => onSnapToGridChange(Boolean(checked))}
                  disabled={layoutViewMode !== "design"}
                />
                <Label htmlFor="layout-snap-to-grid" className="cursor-pointer text-xs text-gray-700">
                  Snap to grid
                </Label>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Grid size (px)</Label>
                <Input
                  type="number"
                  min={2}
                  max={120}
                  value={gridSize}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value || "8", 10)
                    if (!Number.isFinite(parsed)) return
                    onGridSizeChange(Math.max(2, Math.min(120, parsed)))
                  }}
                  disabled={layoutViewMode !== "design"}
                />
              </div>
            </div>
          </details>

          <details className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Report Frame</summary>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2 rounded-md border px-2 py-2">
                <Checkbox
                  id="layout-report-frame-enabled"
                  checked={Boolean(frame.enabled)}
                  onCheckedChange={(checked) => onFrameChange({ enabled: Boolean(checked) })}
                />
                <Label htmlFor="layout-report-frame-enabled" className="cursor-pointer text-xs text-gray-700">
                  Show frame
                </Label>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Frame scope</Label>
                <Select
                  value={frame.scope === "body" ? "body" : "full"}
                  onValueChange={(value: "full" | "body") => onFrameChange({ scope: value })}
                  disabled={!frame.enabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full page</SelectItem>
                    <SelectItem value="body">Body only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Outer inset (Top / Right / Bottom / Left)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min={0} max={400} value={frame.insetTop} disabled={!frame.enabled} onChange={(e) => onFrameChange({ insetTop: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.insetRight} disabled={!frame.enabled} onChange={(e) => onFrameChange({ insetRight: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.insetBottom} disabled={!frame.enabled} onChange={(e) => onFrameChange({ insetBottom: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.insetLeft} disabled={!frame.enabled} onChange={(e) => onFrameChange({ insetLeft: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Inner padding guide (Top / Right / Bottom / Left)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min={0} max={400} value={frame.paddingTop} disabled={!frame.enabled} onChange={(e) => onFrameChange({ paddingTop: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.paddingRight} disabled={!frame.enabled} onChange={(e) => onFrameChange({ paddingRight: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.paddingBottom} disabled={!frame.enabled} onChange={(e) => onFrameChange({ paddingBottom: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                  <Input type="number" min={0} max={400} value={frame.paddingLeft} disabled={!frame.enabled} onChange={(e) => onFrameChange({ paddingLeft: Number.parseInt(e.target.value || "0", 10) || 0 })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Border width</Label>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={frame.borderWidth}
                    disabled={!frame.enabled}
                    onChange={(event) => onFrameChange({ borderWidth: Number.parseInt(event.target.value || "1", 10) || 1 })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Border radius</Label>
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    value={frame.cornerRadius}
                    disabled={!frame.enabled}
                    onChange={(event) => onFrameChange({ cornerRadius: Number.parseInt(event.target.value || "0", 10) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Border style</Label>
                  <Select
                    value={frame.borderStyle === "dashed" ? "dashed" : "solid"}
                    onValueChange={(value: "solid" | "dashed") => onFrameChange({ borderStyle: value })}
                    disabled={!frame.enabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Solid</SelectItem>
                      <SelectItem value="dashed">Dashed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Border color</Label>
                  <Input
                    type="color"
                    value={String(frame.borderColor || "#334155")}
                    disabled={!frame.enabled}
                    onChange={(event) => onFrameChange({ borderColor: event.target.value })}
                  />
                </div>
              </div>
            </div>
          </details>

          <details className="rounded-md border bg-white p-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Page Settings</summary>
            <div className="mt-2 space-y-2">
              <div className="space-y-2">
            <Label className="text-xs text-gray-500">Page Size</Label>
            <Select
              value={layout.pageSize}
              onValueChange={(value: ReportTemplateLayoutDto["pageSize"]) => onPageSizeChange(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A4">A4</SelectItem>
                <SelectItem value="A3">A3</SelectItem>
                <SelectItem value="Letter">Letter</SelectItem>
              </SelectContent>
            </Select>
              </div>

              <div className="space-y-2">
            <Label className="text-xs text-gray-500">Orientation</Label>
            <Select
              value={layout.orientation}
              onValueChange={(value: ReportTemplateLayoutDto["orientation"]) => onOrientationChange(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="portrait">Portrait</SelectItem>
                <SelectItem value="landscape">Landscape</SelectItem>
              </SelectContent>
            </Select>
              </div>

              {layoutViewMode === "preview" ? (
                <div className="space-y-2 border-t pt-3">
              <Label className="text-xs text-gray-500">Preview Page</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onPreviewPageChange(Math.max(0, previewPageIndex - 1))}
                  disabled={previewPageIndex <= 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-gray-600">
                  {previewPageIndex + 1} / {Math.max(1, previewTotalPages)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onPreviewPageChange(Math.min(Math.max(1, previewTotalPages) - 1, previewPageIndex + 1))}
                  disabled={previewPageIndex >= Math.max(1, previewTotalPages) - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
                </div>
              ) : null}
            </div>
          </details>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-1 pb-3">
          <CardTitle className="text-base">{layoutViewMode === "design" ? "Canvas" : "Live WYSIWYG Preview"}</CardTitle>
          <p className="text-xs text-gray-500">
            {layoutViewMode === "design"
              ? "Drop to place widget. Drag selected widget to move position."
              : "Read-only preview from current layout (1:1 same canvas position)."}
          </p>
          {layoutViewMode === "design" ? (
            <p className="text-xs font-medium text-blue-700">
              Editing Section: {sectionLabel}
            </p>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="overflow-auto rounded-md border bg-gray-50 p-4">
            <div
              className="relative mx-auto border bg-white shadow-sm"
              style={{
                width: activeCanvasMetrics.width,
                height: activeCanvasMetrics.height,
                minWidth: activeCanvasMetrics.width,
                minHeight: activeCanvasMetrics.height,
              }}
              onClick={handleCanvasSurfaceClick}
            >
              {layoutViewMode === "design" ? (
                <div
                  ref={canvasRef}
                  className="absolute left-0 right-0 z-0"
                  style={{ top: designSectionOffsetY, height: editCanvasMetrics.height }}
                  onDrop={onCanvasDrop}
                  onDragOver={onCanvasDragOver}
                />
              ) : null}

              {showDesignGrid ? (
                <div
                  className="absolute left-0 right-0 pointer-events-none z-[1]"
                  style={{
                    top: designSectionOffsetY,
                    height: editCanvasMetrics.height,
                    ...designGridStyle,
                  }}
                />
              ) : null}

              {frame.enabled
                ? Array.from({ length: framePageCount }).map((_, pageIndex) => {
                    const pageTop = pageIndex * fullCanvasMetrics.height
                    const areaTop = pageTop + frameAreaTopOffsetPerPage
                    const areaHeight = frameAreaHeightPerPage
                    const outerLeft = frame.insetLeft
                    const outerTop = areaTop + frame.insetTop
                    const outerWidth = Math.max(0, fullCanvasMetrics.width - frame.insetLeft - frame.insetRight)
                    const outerHeight = Math.max(0, areaHeight - frame.insetTop - frame.insetBottom)
                    const innerLeft = outerLeft + frame.paddingLeft
                    const innerTop = outerTop + frame.paddingTop
                    const innerWidth = Math.max(0, outerWidth - frame.paddingLeft - frame.paddingRight)
                    const innerHeight = Math.max(0, outerHeight - frame.paddingTop - frame.paddingBottom)
                    if (outerWidth <= 0 || outerHeight <= 0) return null

                    return (
                      <div key={`report-frame-${pageIndex}`} className="pointer-events-none absolute inset-0 z-[2]">
                        <div
                          className="absolute"
                          style={{
                            left: outerLeft,
                            top: outerTop,
                            width: outerWidth,
                            height: outerHeight,
                            border: `${Math.max(1, frame.borderWidth)}px ${frame.borderStyle === "dashed" ? "dashed" : "solid"} ${frame.borderColor}`,
                            borderRadius: frame.cornerRadius,
                          }}
                        />
                        {(frame.paddingTop > 0 || frame.paddingRight > 0 || frame.paddingBottom > 0 || frame.paddingLeft > 0) &&
                        innerWidth > 0 &&
                        innerHeight > 0 ? (
                          <div
                            className="absolute"
                            style={{
                              left: innerLeft,
                              top: innerTop,
                              width: innerWidth,
                              height: innerHeight,
                              border: "1px dashed rgba(30, 64, 175, 0.45)",
                              borderRadius: Math.max(0, frame.cornerRadius - 2),
                            }}
                          />
                        ) : null}
                      </div>
                    )
                  })
                : null}

              {!hasAnyWidgets ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
                  No widgets yet. Drag from toolbox.
                </div>
              ) : null}

              {layoutViewMode === "design" ? (
                <>
                  {headerEnabled ? (
                    <div
                      className={[
                        "absolute left-0 right-0 z-0 border-b border-blue-200/70 bg-blue-50/50",
                        designSection === "header" ? "ring-1 ring-inset ring-blue-300" : "",
                      ].join(" ")}
                      style={{ top: 0, height: headerHeight }}
                      onClick={() => handleSectionShortcut("header")}
                      onDrop={(event) =>
                        onCanvasDrop(event, "header", { width: fullCanvasMetrics.width, height: Math.max(60, headerHeight) })
                      }
                      onDragOver={onCanvasDragOver}
                      title="Click to edit Header"
                    />
                  ) : null}
                  <div
                    className={[
                      "absolute left-0 right-0 z-0 border-y border-emerald-200/70 bg-emerald-50/30",
                      designSection === "body" ? "ring-1 ring-inset ring-emerald-300" : "",
                    ].join(" ")}
                    style={{ top: bodyOffsetY, height: designBodyMode ? editCanvasMetrics.height : Math.max(240, fullCanvasMetrics.height - bodyOffsetY - (footerEnabled ? footerHeight : 0)) }}
                    onClick={() => handleSectionShortcut("body")}
                    onDrop={(event) =>
                      onCanvasDrop(
                        event,
                        "body",
                        { width: fullCanvasMetrics.width, height: editCanvasMetrics.height },
                        {
                          flowSectionId: activeBodyFlowSectionId,
                          manualPage: designBodyPage,
                        },
                      )
                    }
                    onDragOver={onCanvasDragOver}
                    title={`Click to edit Body page ${designBodyPage}`}
                  />
                  {footerEnabled ? (
                    <div
                      className={[
                        "absolute left-0 right-0 z-0 border-t border-amber-200/70 bg-amber-50/50",
                        designSection === "footer" ? "ring-1 ring-inset ring-amber-300" : "",
                      ].join(" ")}
                      style={{ top: designFooterOffsetY, height: footerHeight }}
                      onClick={() => handleSectionShortcut("footer")}
                      onDrop={(event) =>
                        onCanvasDrop(event, "footer", { width: fullCanvasMetrics.width, height: Math.max(60, footerHeight) })
                      }
                      onDragOver={onCanvasDragOver}
                      title="Click to edit Footer"
                    />
                  ) : null}
                  {sectionSurfaceItems.map((section) => {
                    if (!section.enabled || section.height <= 0) return null
                    return (
                      <div
                        key={`section-surface-label-${section.key}`}
                        className="pointer-events-none absolute left-3 z-[3]"
                        style={{ top: section.top + 8 }}
                      >
                        <div className="inline-flex items-center gap-2 rounded border bg-white/95 px-2.5 py-1 text-xs text-slate-700 shadow-sm">
                          <span className="font-medium">{section.label}</span>
                          <span className="text-slate-400">{section.widgetCount} widget(s)</span>
                        </div>
                      </div>
                    )
                  })}
                </>
              ) : null}

              {layoutViewMode === "design" && designSection === "body"
                ? designFlowSectionBoxes.map((box) => {
                    const isMoving = movingSectionId === box.sectionId
                    const isResizing = resizingSectionId === box.sectionId
                    return (
                      <div
                        key={`section-box-${box.sectionId}`}
                        className={[
                        "absolute rounded-md border-2 border-dashed border-blue-300 bg-blue-50/20 pointer-events-none",
                          activeBodyFlowSectionId === box.sectionId ? "ring-2 ring-inset ring-blue-400 bg-blue-50/30" : "",
                          isMoving || isResizing ? "opacity-80" : "opacity-100",
                        ].join(" ")}
                        style={{
                          left: box.x,
                          top: box.y + designSectionOffsetY,
                          width: box.width,
                          height: box.height,
                          zIndex: 5,
                        }}
                      >
                        <div
                          className="absolute left-2 top-2 pointer-events-auto inline-flex items-center gap-2 rounded-md border bg-white/95 px-2 py-1 text-xs text-gray-700 shadow-sm cursor-move"
                          onClick={(event) => {
                            event.stopPropagation()
                            onActiveBodyFlowSectionChange(box.sectionId)
                          }}
                          onMouseDown={(event) => onFlowSectionMouseDown(box.sectionId, event)}
                          title="Drag to move all widgets in section"
                        >
                          <Move className="h-3 w-3" />
                          <span className="font-medium">{box.name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {box.breakMode === "newPage" ? "new page" : "flow"}
                          </Badge>
                          <span className="text-[10px] text-gray-500">min {box.minHeight}px</span>
                          <span className="text-[10px] text-gray-500">{box.widgetCount} widgets</span>
                          {activeBodyFlowSectionId === box.sectionId ? (
                            <Badge variant="default" className="text-[10px]">active</Badge>
                          ) : null}
                          <button
                            type="button"
                            className="pointer-events-auto ml-1 inline-flex items-center rounded border bg-white px-1 py-0.5 text-[10px] hover:bg-gray-50"
                            onMouseDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              onToggleFlowSectionCollapsed(box.sectionId)
                            }}
                            title={box.isCollapsed ? "Expand section" : "Collapse section"}
                          >
                            {box.isCollapsed ? (
                              <ChevronRight className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                        {!box.isCollapsed ? (
                          <div
                            className="absolute left-1/2 bottom-0 pointer-events-auto h-2 w-10 -translate-x-1/2 translate-y-1/2 cursor-ns-resize rounded-full border border-blue-400 bg-blue-100"
                            onMouseDown={(event) => onFlowSectionResizeMouseDown(box.sectionId, event)}
                            title="Resize section height"
                          />
                        ) : null}
                      </div>
                    )
                  })
                : null}

              {renderEntries.map(({ widget, section, yOffset, renderYOverride, renderHeightOverride, editable, ghost, tableRows }) => {
                const isWidgetLocked = getWidgetLocked(widget)
                const widgetFlowSectionId =
                  layoutViewMode === "design" && designSection === "body" && editable
                    ? getWidgetFlowSectionId(widget, primaryFlowSectionId)
                    : null
                const flowSectionBox =
                  widgetFlowSectionId != null
                    ? designFlowSectionBoxes.find((box) => box.sectionId === widgetFlowSectionId)
                    : null
                if (
                  layoutViewMode === "design" &&
                  designSection === "body" &&
                  editable &&
                  !ghost &&
                  flowSectionBox?.isCollapsed
                ) {
                  return null
                }
                const safeX = toFiniteNumber(widget.x, 0)
                const safeY = Number.isFinite(renderYOverride) ? Number(renderYOverride) : toFiniteNumber(widget.y, 0)
                const safeWidth = Math.max(1, toFiniteNumber(widget.width, 240))
                const safeHeight = Math.max(
                  1,
                  Number.isFinite(renderHeightOverride) ? Number(renderHeightOverride) : toFiniteNumber(widget.height, 56),
                )
                const isActiveDesignWidget =
                  layoutViewMode === "design" &&
                  editable &&
                  (selectedWidgetId === widget.id || movingWidgetId === widget.id || resizingWidgetId === widget.id)
                const commonStyle = {
                  left: safeX,
                  top: safeY + yOffset,
                  width: safeWidth,
                  height: safeHeight,
                  zIndex: isActiveDesignWidget ? ACTIVE_WIDGET_Z_INDEX : (widget.zIndex || 1),
                }
                const widgetBorderStyle = buildWidgetBorderStyle(widget)
                const cornerRadius = getWidgetCornerRadius(widget)
                const textAlign = getWidgetTextAlign(widget)
                const verticalAlign = getWidgetVerticalAlign(widget)
                const widgetTypography = getWidgetTypographyConfig(widget)
                const lineConfig = getWidgetLineConfig(widget)
                const isShapeWidget = widget.type === "line" || widget.type === "rectangle"
                const rectangleFillColor =
                  widget.type === "rectangle" ? String(widget.props?.fillColor || "transparent") : "transparent"
                const justifyByHorizontal =
                  textAlign === "center"
                    ? "center"
                    : textAlign === "right"
                      ? "flex-end"
                      : "flex-start"
                const alignByVertical =
                  verticalAlign === "center"
                    ? "center"
                    : verticalAlign === "bottom"
                      ? "flex-end"
                      : "flex-start"
                const contentPadding = Math.max(0, Math.min(4, Math.floor(Math.min(safeWidth, safeHeight) / 10)))
                const widgetFrameStyle: CSSProperties = {
                  ...commonStyle,
                  ...widgetBorderStyle,
                  borderRadius: cornerRadius,
                  backgroundColor: widget.type === "rectangle" ? rectangleFillColor : undefined,
                }
                const widgetTypographyStyle: CSSProperties = {
                  fontSize: widgetTypography.fontSize,
                  fontWeight: widgetTypography.fontWeight,
                  fontStyle: widgetTypography.fontStyle,
                }
                const designContent = (() => {
                  if (widget.type === "text") {
                    return String(widget.props?.text || "") || "-"
                  }
                  if (widget.type === "field") {
                    const fieldKey = String(widget.props?.fieldKey || "")
                    return String(widget.props?.label || fieldLabelMap.get(fieldKey) || "Field")
                  }
                  if (widget.type === "image") {
                    return "Image"
                  }
                  if (widget.type === "table") {
                    return "Table"
                  }
                  if (widget.type === "line") {
                    return "Line"
                  }
                  if (widget.type === "rectangle") {
                    return "Rectangle"
                  }
                  return toWidgetTitle(widget)
                })()

                if (layoutViewMode === "design") {
                  const imageBindingType = String(widget.props?.bindingType || "static")
                  const imageStaticUrl = String(widget.props?.staticUrl || "")
                  const imageFieldKey = String(widget.props?.fieldKey || "")
                  const imageFit = getWidgetImageFit(widget)
                  const imageBoundValue = getFieldDisplay(imageFieldKey, String(widget.props?.datasourceId || ""))
                  const designImageUrl =
                    imageBindingType === "field"
                      ? (isLikelyImageUrl(imageBoundValue) ? imageBoundValue : "")
                      : (isLikelyImageUrl(imageStaticUrl) ? imageStaticUrl : "")
                  return (
                    <div
                      key={widget.id}
                      className={[
                        "absolute rounded-md shadow-sm box-border overflow-visible",
                        isShapeWidget ? "" : "bg-white/95 p-2",
                        editable
                          ? isWidgetLocked
                            ? "cursor-default select-none"
                            : "cursor-move select-none"
                          : "cursor-default select-none",
                        ghost ? "opacity-70" : "",
                        selectedWidgetId === widget.id ? "outline outline-2 outline-blue-500 ring-2 ring-blue-100" : "",
                        movingWidgetId === widget.id ? "opacity-70" : "",
                        resizingWidgetId === widget.id ? "cursor-se-resize" : "",
                      ].join(" ").trim()}
                      style={widgetFrameStyle}
                      draggable={false}
                      onMouseDown={(event) => {
                        if (!editable) return
                        if (isWidgetLocked) return
                        onWidgetMouseDown(widget, event)
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        if (layoutViewMode === "design" && !editable && ghost && section !== designSection) {
                          onDesignSectionChange(section)
                          onWidgetClick(widget.id)
                          return
                        }
                        if (!editable) return
                        onWidgetClick(widget.id)
                      }}
                    >
                      <div
                        className="flex h-full w-full"
                        style={{
                          alignItems: alignByVertical,
                          justifyContent: justifyByHorizontal,
                        }}
                      >
                        {widget.type === "line" ? (
                          <div className="flex h-full w-full items-center justify-center px-1">
                            <div
                              className="w-full"
                              style={{
                                borderTop: `${lineConfig.lineThickness}px ${lineConfig.lineStyle} ${lineConfig.lineColor}`,
                                transform: `rotate(${lineConfig.lineAngle}deg)`,
                                transformOrigin: "center",
                              }}
                            />
                          </div>
                        ) : widget.type === "rectangle" ? null : widget.type === "image" ? (
                          designImageUrl ? (
                            <img
                              src={designImageUrl}
                              alt={imageFieldKey || "design-image"}
                              className="h-full w-full"
                              style={{ objectFit: imageFit }}
                              draggable={false}
                            />
                          ) : (
                            <div
                              className="w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-gray-700"
                              style={{ textAlign, ...widgetTypographyStyle }}
                            >
                              {designContent}
                            </div>
                          )
                        ) : (
                          <div
                            className="w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-gray-700"
                            style={{ textAlign, ...widgetTypographyStyle }}
                          >
                            {designContent}
                          </div>
                        )}
                      </div>
                      <div className="absolute right-0 top-0 z-10 flex items-center gap-1 -translate-y-full">
                        <Badge variant="outline">{widget.type}</Badge>
                        {selectedWidgetId === widget.id && editable ? (
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
                            onMouseDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              onDuplicateWidget(widget.id)
                            }}
                            title={`Duplicate ${toWidgetTitle(widget)}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {selectedWidgetId === widget.id && editable && !isWidgetLocked ? (
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50"
                            onMouseDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              onRemoveWidget(widget.id)
                            }}
                            title={`Delete ${toWidgetTitle(widget)}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                      {layoutViewMode === "design" ? (
                        <div className="absolute left-0 top-0 z-10 -translate-y-full">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded border bg-white px-1.5 py-0.5 text-[10px] text-slate-600 shadow-sm hover:bg-slate-50"
                            draggable
                            onMouseDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onDragStart={(event) =>
                              onWidgetTransferDragStart(
                                section,
                                widget.id,
                                widget.type,
                                Number(widget.width) || 240,
                                Number(widget.height) || 56,
                                event,
                              )
                            }
                            title={`Drag to move ${toWidgetTitle(widget)} to another section`}
                          >
                            <Move className="h-3 w-3" />
                            Move
                          </button>
                        </div>
                      ) : null}
                      {isWidgetLocked ? (
                        <div className="absolute left-14 top-0 z-10 -translate-y-full">
                          <Badge variant="secondary">locked</Badge>
                        </div>
                      ) : null}
                      {selectedWidgetId === widget.id && editable && !isWidgetLocked ? (
                        <>
                          <div
                            className="absolute right-0 top-2 bottom-4 w-2 cursor-e-resize"
                            data-resize-handle="true"
                            onMouseDown={(event) => onResizeMouseDown(widget, event, "width")}
                            title="Resize width"
                          />
                          <div
                            className="absolute left-2 right-4 bottom-0 h-2 cursor-s-resize"
                            data-resize-handle="true"
                            onMouseDown={(event) => onResizeMouseDown(widget, event, "height")}
                            title="Resize height"
                          />
                          <div
                            className="absolute bottom-1 right-1 h-3 w-3 rounded-sm border border-blue-500 bg-blue-400/80 cursor-se-resize"
                            data-resize-handle="true"
                            onMouseDown={(event) => onResizeMouseDown(widget, event, "both")}
                            title="Resize"
                          />
                        </>
                      ) : null}
                    </div>
                  )
                }

                if (widget.type === "text") {
                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-hidden rounded-sm bg-white text-sm text-gray-900 box-border"
                      style={{
                        ...widgetFrameStyle,
                        display: "flex",
                        alignItems: alignByVertical,
                        justifyContent: justifyByHorizontal,
                        padding: contentPadding,
                      }}
                    >
                      <div
                        className="w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                        style={{ textAlign, ...widgetTypographyStyle }}
                      >
                        {String(widget.props?.text || "") || "-"}
                      </div>
                    </div>
                  )
                }

                if (widget.type === "field") {
                  const fieldKey = String(widget.props?.fieldKey || "")
                  const fieldLabel = resolveWidgetLabel(widget, fieldLabelMap.get(fieldKey) || "Field")
                  const fieldValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
                  const hasFieldLabel = fieldLabel.trim().length > 0
                  const isFlowField = getWidgetPositionMode(widget) === "flow"
                  const renderedFieldHeight = estimateFieldRenderedHeight(widget, fieldLabel, fieldValue)
                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-hidden rounded-sm bg-white box-border"
                      style={{
                        ...widgetFrameStyle,
                        height: isFlowField ? "auto" : safeHeight,
                        minHeight: isFlowField ? renderedFieldHeight : safeHeight,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: alignByVertical,
                        padding: contentPadding,
                      }}
                    >
                      {hasFieldLabel ? (
                        <div
                          className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-gray-500"
                          style={{ textAlign, ...widgetTypographyStyle }}
                        >
                          {fieldLabel}
                        </div>
                      ) : null}
                      <div
                        className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-gray-900"
                        style={{ textAlign, ...widgetTypographyStyle }}
                      >
                        {fieldValue}
                      </div>
                    </div>
                  )
                }

                if (widget.type === "image") {
                  const bindingType = String(widget.props?.bindingType || "static")
                  const staticUrl = String(widget.props?.staticUrl || "")
                  const fieldKey = String(widget.props?.fieldKey || "")
                  const imageFit = getWidgetImageFit(widget)
                  const boundValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
                  const imageUrl =
                    bindingType === "field"
                      ? (isLikelyImageUrl(boundValue) ? boundValue : "")
                      : (isLikelyImageUrl(staticUrl) ? staticUrl : "")

                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-hidden rounded-sm bg-gray-100 box-border"
                      style={widgetFrameStyle}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={fieldKey || "report-image"}
                          className="h-full w-full"
                          style={{ objectFit: imageFit }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-gray-500">
                          {bindingType === "field"
                            ? `Image field: ${fieldKey || "-"}`
                            : "Image placeholder"}
                        </div>
                      )}
                    </div>
                  )
                }

                if (widget.type === "line") {
                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-hidden rounded-sm bg-transparent box-border"
                      style={{
                        ...widgetFrameStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="w-full"
                        style={{
                          borderTop: `${lineConfig.lineThickness}px ${lineConfig.lineStyle} ${lineConfig.lineColor}`,
                          transform: `rotate(${lineConfig.lineAngle}deg)`,
                          transformOrigin: "center",
                        }}
                      />
                    </div>
                  )
                }

                if (widget.type === "rectangle") {
                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-hidden rounded-sm box-border"
                      style={{
                        ...widgetFrameStyle,
                        backgroundColor: rectangleFillColor,
                      }}
                    />
                  )
                }

                if (widget.type === "table") {
                  const columns = String(widget.props?.columns || "")
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                  const rows =
                    tableRows && tableRows.length > 0
                      ? tableRows
                      : (columns.length > 0 ? [columns.map(() => "-")] : [["-"]])
                  const isFlowTable = getWidgetPositionMode(widget) === "flow"
                  const renderedTableHeight = estimateTableRenderedHeight(widget, columns, rows)
                  const columnWidths = getTableColumnWidths(widget, columns)
                  const hasColumnWidths = columns.some((columnKey) => {
                    const width = columnWidths[columnKey]
                    return Number.isFinite(width) && width > 0
                  })
                  const tableFrameStyle: CSSProperties = {
                    ...widgetFrameStyle,
                    height: "auto",
                    minHeight: isFlowTable ? renderedTableHeight : widget.height,
                  }
                  return (
                    <div
                      key={`preview-${widget.id}`}
                      className="absolute overflow-visible rounded-sm bg-white box-border"
                      style={tableFrameStyle}
                    >
                      <table
                        className="w-full border-collapse text-[11px]"
                        style={{ tableLayout: hasColumnWidths ? "fixed" : "auto" }}
                      >
                        {columns.length > 0 ? (
                          <colgroup>
                            {columns.map((columnKey) => {
                              const width = columnWidths[columnKey]
                              return (
                                <col
                                  key={`col-${widget.id}-${columnKey}`}
                                  style={Number.isFinite(width) && width > 0 ? { width: `${width}%` } : undefined}
                                />
                              )
                            })}
                          </colgroup>
                        ) : null}
                        <thead>
                          <tr className="bg-gray-50">
                            {columns.length === 0 ? (
                              <th className="border border-gray-200 px-2 py-1 text-left font-medium text-gray-600">
                                No columns
                              </th>
                            ) : (
                              columns.map((columnKey) => (
                                <th
                                  key={`head-${widget.id}-${columnKey}`}
                                  className="border border-gray-200 px-2 py-1 text-left font-medium text-gray-600 whitespace-normal break-words align-top"
                                >
                                  {getTableColumnLabel(widget, columnKey)}
                                </th>
                              ))
                            )}
                          </tr>
                        </thead>
                        {columns.length > 0 ? (
                          <tbody>
                            {(rows.length > 0 ? rows : [columns.map(() => "-")]).map((rowValues, rowIndex) => (
                              <tr key={`row-${widget.id}-${rowIndex}`}>
                                {rowValues.map((cellValue, cellIndex) => (
                                  <td
                                    key={`cell-${widget.id}-${rowIndex}-${cellIndex}`}
                                    className="border border-gray-200 px-2 py-1 text-gray-700 whitespace-normal break-words align-top"
                                  >
                                    {cellValue}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        ) : null}
                      </table>
                    </div>
                  )
                }

                return null
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
